import { Loader2 } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { captureEvent } from '@/posthog-analytics/posthog';
import type { PrefillSource } from './profileCompletion';

export type NameCaptureVariant = 'login-step' | 'interstitial' | 'settings';

interface NameCaptureFormProps {
  initialFirstName: string;
  initialLastName: string;
  suggestionSource: PrefillSource;
  variant: NameCaptureVariant;
  isSaving: boolean;
  error?: string | null;
  allowSkip: boolean;
  submitLabel?: string;
  onSubmit: (firstName: string, lastName: string) => void;
  onSkip?: () => void;
}

const INPUT_CLASS =
  'w-full border border-gray-300 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all text-sm text-gray-900 placeholder-gray-400';

/** Asks for a name and nothing else. No fetching, no auth, no router, so the
 *  login step, the route gate and the profile page all render the same thing. */
export const NameCaptureForm: React.FC<NameCaptureFormProps> = ({
  initialFirstName,
  initialLastName,
  suggestionSource,
  variant,
  isSaving,
  error = null,
  allowSkip,
  submitLabel,
  onSubmit,
  onSkip,
}) => {
  const [firstName, setFirstName] = useState(initialFirstName);
  const [lastName, setLastName] = useState(initialLastName);
  const firstNameRef = useRef<HTMLInputElement>(null);

  // Select rather than just focus: when the prefill is a guess off the email it
  // is usually wrong, and selecting makes replacing it a single keystroke.
  useEffect(() => {
    firstNameRef.current?.focus();
    if (suggestionSource === 'suggestion') firstNameRef.current?.select();
  }, [suggestionSource]);

  useEffect(() => {
    // Editing from the profile page is not a prompt, so it stays out of the
    // prompt funnel.
    if (variant === 'settings') return;
    captureEvent('profile_name_prompt_viewed', {
      surface: variant,
      prefill_source: suggestionSource,
    });
  }, [variant, suggestionSource]);

  const trimmedFirstName = firstName.trim();
  const trimmedLastName = lastName.trim();
  const canSubmit = trimmedFirstName.length > 0 && trimmedLastName.length > 0 && !isSaving;

  const submit = () => {
    if (!canSubmit) return;
    onSubmit(trimmedFirstName, trimmedLastName);
  };

  return (
    <div className="space-y-4">
      {variant !== 'settings' && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900">What should we call you?</h2>
          <p className="mt-1 text-sm text-gray-500">
            This is the name recruiters and interviewers will see.
          </p>
        </div>
      )}

      <div className="space-y-2.5">
        <div>
          <label htmlFor="name-capture-first" className="block text-sm font-medium text-gray-700 mb-1.5">
            First name
          </label>
          <input
            id="name-capture-first"
            data-testid="name-capture-first"
            ref={firstNameRef}
            type="text"
            autoComplete="given-name"
            maxLength={150}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submit();
            }}
            className={INPUT_CLASS}
          />
        </div>

        <div>
          <label htmlFor="name-capture-last" className="block text-sm font-medium text-gray-700 mb-1.5">
            Last name
          </label>
          <input
            id="name-capture-last"
            data-testid="name-capture-last"
            type="text"
            autoComplete="family-name"
            maxLength={150}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submit();
            }}
            className={INPUT_CLASS}
          />
        </div>

        {suggestionSource === 'suggestion' && (
          <p className="text-xs text-gray-500">
            We guessed this from your email address, please correct it.
          </p>
        )}
      </div>

      {error && (
        <p role="alert" className="text-sm text-red-600">
          {error}
        </p>
      )}

      <div className="space-y-2">
        <button
          type="button"
          data-testid="name-capture-submit"
          onClick={submit}
          disabled={!canSubmit}
          className="w-full bg-primary-500 hover:bg-primary-600 text-white py-2.5 px-4 rounded-xl flex items-center justify-center gap-2 transition-all font-medium text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {isSaving ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Saving...
            </>
          ) : (
            (submitLabel ?? (variant === 'settings' ? 'Save' : 'Continue'))
          )}
        </button>

        {allowSkip && onSkip && (
          <button
            type="button"
            data-testid="name-capture-skip"
            onClick={onSkip}
            // Never disabled: skipping is local state, not a request. A save that
            // hangs rather than fails would otherwise leave the interstitial
            // covering the whole app with no way out.
            className="w-full text-center text-sm text-gray-500 hover:text-gray-700 py-1.5 transition-colors"
          >
            Skip for now
          </button>
        )}
      </div>
    </div>
  );
};

export default NameCaptureForm;
